/**
 * Layout classes for the document authoring stepper.
 *
 * Structure only — every colour is a semantic token from `src/styles/globals.css`.
 * The stepper's own rail is styled by `@/shared/app-stepper-form`; these classes
 * cover only what is specific to authoring a document: the page frame, the
 * review read-back, and the "already created" notice.
 */
export const styles = {
  /**
   * No padding — the shell's `<main>` owns page gutters.
   *
   * `min-h-0` is load-bearing: the stepper inside is a fixed-height panel whose
   * body scrolls, and a flex parent that refuses to shrink below its content
   * would let the panel grow the page instead of scrolling inside itself.
   */
  page: 'flex min-h-0 flex-1 flex-col gap-4',

  /**
   * The breadcrumb strip above the stepper.
   *
   * The stepper renders its own title, so this carries navigation only. It is
   * deliberately NOT a card: the previous design wrapped the title in an accent
   * card that duplicated the stepper's heading and pushed the first field below
   * the fold.
   */
  breadcrumbsRow: 'flex flex-wrap items-center justify-between gap-3',

  /** The read-back grid on the review step. */
  reviewGrid: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
  reviewTerm: 'text-xs tracking-wide text-content-muted uppercase',
  reviewValue: 'text-sm text-content',

  /**
   * The "the document already exists" notice.
   *
   * Shown when an earlier step succeeded and a later one failed. It is a
   * `caution` wash rather than a `critical` one: nothing is broken, but the
   * operator must know the work is half-done and where the other half lives.
   */
  partialNotice:
    'flex flex-col gap-2 rounded-surface border border-border border-l-4 border-l-caution bg-caution-soft p-4',
  partialNoticeTitle: 'text-sm font-medium text-content',
  partialNoticeBody: 'text-xs text-content-muted',
  partialNoticeActions: 'flex flex-wrap items-center gap-2',

  /** The content preview on the review step — the exact text a subject will read. */
  preview:
    'max-h-64 overflow-y-auto rounded-surface border border-border bg-surface-sunken p-4 text-sm whitespace-pre-wrap text-content',
} as const;
